import { useEffect, useMemo, useState } from 'react';
import { useDateFormat, useTimeFormat } from '../context/LocaleContext';
import { todayISO } from '../domain/clock';
import { addDays } from '../domain/recurrence';
import { studentSearchText, studentStatus } from '../domain/people';
import useNames from './useNames';
import { allRowsSelected, pruneSelection, statusFilterMatches } from '../domain/roster';
import { defaultColumnIds, STATUSES } from '../components/students/columns';

// Everything the Students table needs to decide what it is showing: the
// filters, the sort, which columns are on, and which rows are ticked.
//
// It is a hook rather than page state so the page itself is assembly, and so
// the one rule that is easy to get wrong — selection must not survive a change
// to what is on screen — lives next to the filters it depends on.

export default function useStudentList({ students, occurrencesBetween }) {
  // The list sorts by the studio's Student Name Format, so the order matches
  // the names on the screen — sorting by surname under "First Last" reads as
  // unsorted.
  const names = useNames();
  // A set of statuses (#574); the default is `active` alone.
  const [statusFilter, setStatusFilterRaw] = useState(() => new Set(['active']));
  const [search, setSearch] = useState('');
  const [sortAsc, setSortAsc] = useState(true);
  const [columnIds, setColumnIds] = useState(() => new Set(defaultColumnIds()));
  const [selected, setSelected] = useState(() => new Set());

  // Next lesson per student, from the live series expansion rather than a
  // stored field — a stored "next lesson" goes stale the moment a series moves.
  const formatDate = useDateFormat();
  const formatTime = useTimeFormat();

  const nextLessonByStudent = useMemo(() => {
    const today = todayISO();
    const end = addDays(today, 90);
    const map = new Map();
    // A lesson marked `Teacher Cancelled` is still this student's next
    // scheduled slot and is named as one; a removed lesson is not produced by
    // its rule (#152).
    for (const o of occurrencesBetween(today, end)) {
      for (const sid of o.studentIds) {
        // The studio's date and clock, as the calendar shows the lesson.
        if (!map.has(sid)) map.set(sid, `${formatDate(o.date)} ${formatTime(o.startTime)}`);
      }
    }
    return map;
  }, [occurrencesBetween, formatDate, formatTime]);

  const rows = useMemo(() => {
    let list = students.filter((s) =>
      statusFilterMatches(studentStatus(s.status), statusFilter, STATUSES));
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((s) => studentSearchText(s).includes(q));
    }
    return [...list].sort((a, b) => {
      const an = names.studentSortKey(a);
      const bn = names.studentSortKey(b);
      return sortAsc ? an.localeCompare(bn) : bn.localeCompare(an);
    });
  }, [students, statusFilter, search, sortAsc, names]);

  // Drop any selection that is no longer on screen when the filter or search
  // changes. Without this a bulk action silently applies to students the
  // teacher can no longer see, and the header's select-all tick — which
  // compares sizes — reads as unticked while rows are still selected.
  useEffect(() => {
    setSelected((prev) => pruneSelection(prev, rows.map((r) => r.id)));
  }, [rows]);

  // One status in or out of the set. Never left empty by the caller — the
  // toolbar itself does not refuse a last unchecked box, same as the column
  // picker only refuses the locked column — but an empty set is a valid
  // (if useless) filter rather than a special case here.
  const toggleStatusFilter = (status) => setStatusFilterRaw((prev) => {
    const next = new Set(prev);
    if (next.has(status)) next.delete(status); else next.add(status);
    return next;
  });

  // The whole set at once — "All statuses" and "None" on the picker.
  const setStatusFilter = (statuses) => setStatusFilterRaw(new Set(statuses));

  const toggleColumn = (id) => setColumnIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const toggleRow = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const selectAll = (on) => setSelected(on ? new Set(rows.map((r) => r.id)) : new Set());
  const clearSelection = () => setSelected(new Set());

  return {
    rows,
    nextLesson: (id) => nextLessonByStudent.get(id),
    statusFilter, toggleStatusFilter, setStatusFilter,
    search, setSearch,
    sortAsc, toggleSort: () => setSortAsc((v) => !v),
    columnIds, toggleColumn,
    selected, toggleRow, selectAll, clearSelection,
    allSelected: allRowsSelected(rows.length, selected.size),
  };
}
